import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Info, MapPin, Wine } from "lucide-react";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand } from "@/components/GetYourGuide";
import { Reveal } from "@/components/motion";

// The Copenhagen gløgg guide — the six spots from the Christmas page, expanded
// into their own page with map links. Edit SPOTS to add/remove glasses.
const mapsUrl = (name, where) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, ${where}, Copenhagen`)}`;

const SPOTS = [
  {
    name: "Hviids Vinstue",
    where: "Kongens Nytorv 19",
    body:
      "The gløgg institution — a dark, panelled wine cellar running since 1723, famous for its house gløgg served in little glasses. Its gløgg season launches every year on 11 November at 11:11, when the cellar packs out with locals. Be warned: it's strong! Candle-lit, and the one glass you shouldn't leave Copenhagen without.",
  },
  {
    name: "Tivoli's stalls",
    where: "Tivoli Gardens",
    body:
      "Drink it where it's most magical — gløgg in one mitten, a bag of warm æbleskiver in the other, under a million lights. The stalls do both the classic red and white gløgg versions.",
  },
  {
    name: "Hotel d'Angleterre",
    where: "Kongens Nytorv 34",
    body:
      "The posh one. White-tablecloth gløgg in one of Europe's grandest old hotels, with the city's most famous Christmas façade sparkling outside the window.",
  },
  {
    name: "Bar Deco",
    where: "Ny Adelgade 5B",
    body:
      "A tiny, stylish bar tucked into the side streets — where the cocktail crowd gets its gløgg fix in December.",
  },
  {
    name: "Noorbohandelen",
    where: "Torvehallerne, Frederiksborggade 21",
    body:
      "The Torvehallerne deli known for its spice blends — have a glass at the market, then take a bottle of their gløgg mix home.",
  },
  {
    name: "EP9",
    where: "Enghave Plads 9",
    body:
      "Vesterbro's neighbourhood corner bar — gløgg the local way, on a bench outside even when it's freezing.",
  },
];

// Page FAQ — answers stay plain strings so the FAQPage schema below reuses them.
const FAQS = [
  {
    q: "What is gløgg?",
    a: "Denmark's spiced winter warmer — hot red wine steeped with cloves, cinnamon, orange peel and anise, poured over raisins and slivered almonds. A real gløgg is strong, served in small glasses against the dark winter evenings. Most places also do a white version and an alcohol-free one.",
  },
  {
    q: "Where to buy gløgg in Copenhagen?",
    a: "The one glass you shouldn't leave without is at Hviids Vinstue, the 1723 wine cellar on Kongens Nytorv. Tivoli's stalls pour it under a million lights, Hotel d'Angleterre does the posh version, Bar Deco the cocktail-crowd one and EP9 the local Vesterbro one — and Noorbohandelen at Torvehallerne sells their gløgg mix by the bottle to take home. Skip the Christmas market stalls for this one, though — what they pour is mostly sweet mulled wine, not real gløgg.",
  },
  {
    q: "What is the difference between gløgg and mulled wine?",
    a: "Real Danish gløgg is stronger and more heavily spiced than the sweet mulled wine you know from other Christmas markets — and it's always served over raisins and slivered almonds, in small glasses. The sweet stuff ladled out at some market stalls is the tourist version; the spots in this guide pour the real thing.",
  },
  {
    q: "When is gløgg season in Copenhagen?",
    a: "Early November until Christmas — gløgg appears when the markets open and most spots stop serving once the holidays are over. J-Day (the first Friday of November) is the unofficial starting gun.",
  },
  {
    q: "What kind of alcohol is used in gløgg?",
    a: "Red wine is the base — heated gently, never boiled, so the alcohol stays in. What makes real Danish gløgg stronger than mulled wine is the splash of something harder: snaps (aquavit), rum, brandy or port, depending on the house recipe. The white version starts from white wine, and the alcohol-free one skips the lot.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function GloggPage() {
  return (
    <main data-testid="glogg-page" className="bg-[#F9F8F6] text-black">
      <Seo
        title="Copenhagen Gløgg Guide 2026: Where to Drink Gløgg"
        description="The best gløgg in Copenhagen — from the 1723 wine cellar institution to Torvehallerne's spice deli. Six glasses worth seeking out, with addresses and tips."
        path="/glogg"
      />
      <PageSchema
        path="/glogg"
        name="Copenhagen Gløgg Guide"
        description="The best gløgg in Copenhagen — six glasses worth seeking out, with addresses and tips."
        breadcrumb="Gløgg guide"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      <header className="sticky top-0 z-40 bg-black border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-3 flex items-center justify-between">
          <Link
            to="/christmas"
            className="inline-flex items-center gap-2 font-body font-700 text-sm text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} /> Christmas in Copenhagen
          </Link>
          <ShareButton />
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden bg-[#13141A] text-[#F5EFE0]">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1734084959344-d0228c84da82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxEYW5pc2glMjBnbCVDMyVCOGdnfGVufDB8fHx8MTc5MTQ2MTA0OHww&ixlib=rb-4.1.0&q=85" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-28">
          <Reveal y={30}>
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white mb-4">
              GLØGG · THE WINTER WARMER
            </p>
            <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
              The Copenhagen{" "}
              <span className="font-editorial italic font-600">gløgg guide</span>
            </h1>
            <p className="font-body text-base md:text-lg text-[#F5EFE0]/70 mt-6 max-w-2xl">
              Gløgg is Denmark's spiced winter warmer — hot red wine steeped with cloves, cinnamon,
              orange peel and anise, poured over raisins and slivered almonds. And make no mistake: a
              real gløgg is strong, nothing like the sweet "mulled wine" ladled out at the
              Christmas-market stalls. It's a small glass of pure comfort against the grey, dark
              winter evenings. Most places also do a white version and an alcohol-free one.
              These are glasses worth seeking out.
            </p>
            <p data-testid="glogg-updated" className="font-body text-xs uppercase tracking-[0.2em] text-[#F5EFE0]/40 mt-4">
              Last updated: 29 September 2026
            </p>
          </Reveal>
        </div>
      </section>

      {/* the spots */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SPOTS.map((g, i) => (
            <Reveal key={g.name} delay={Math.min(i, 3) * 0.05} y={20}>
              <div
                data-testid={`glogg-guide-spot-${i}`}
                className="h-full bg-white border border-zinc-200 shadow-brut p-6 flex items-start gap-4"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#FDE047] text-black shrink-0">
                  <Wine size={20} />
                </div>
                <div>
                  <h3 className="font-display font-900 tracking-tight text-2xl leading-none">
                    <a
                      href={mapsUrl(g.name, g.where)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`glogg-guide-maps-${i}`}
                      className="hover: hover:underline underline-offset-4 transition-colors"
                    >
                      {g.name}
                    </a>
                  </h3>
                  <p className="mt-2 flex items-center gap-1.5 font-body text-sm text-black/50">
                    <MapPin size={13} className="text-[#DF3226] shrink-0" /> {g.where}
                  </p>
                  <p className="font-body text-sm md:text-base text-black/70 mt-3 leading-relaxed">
                    {g.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal y={20}>
          <Link
            to="/advertise"
            data-testid="glogg-missing"
            className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border-2 border-dashed border-zinc-300 hover:border-[#DF3226] hover:-translate-y-0.5 transition-all p-6 md:p-8"
          >
            <div>
              <span className="inline-flex items-center gap-1.5 font-body font-800 text-[11px] tracking-widest text-[#DF3226]">
                <Wine size={12} /> THIS SPOT IS OPEN
              </span>
              <h3 className="font-display font-900 tracking-tight text-2xl leading-none mt-2">
                Is your gløgg spot missing?
              </h3>
              <p className="font-body text-sm md:text-base text-black/70 mt-2">
                Pouring gløgg this winter? Get in front of the travellers looking for their first one.
              </p>
            </div>
            <span className="shrink-0 inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226]">
              Advertise with us
            </span>
          </Link>
        </Reveal>

        {/* practical notes */}
        <Reveal y={20}>
          <div
            data-testid="glogg-guide-notes"
            className="mt-12 bg-white border border-zinc-200 shadow-brut p-6 md:p-8 flex flex-col md:flex-row gap-6"
          >
            <div className="flex items-start gap-3 flex-1">
              <Info size={18} className="text-[#DF3226] shrink-0 mt-0.5" />
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed">
                <strong className="font-800 text-black">Season:</strong> gløgg flows from early
                November until Christmas — most spots stop serving once the holidays are over.
              </p>
            </div>
            <div className="flex items-start gap-3 flex-1">
              <Info size={18} className="text-[#DF3226] shrink-0 mt-0.5" />
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed">
                <strong className="font-800 text-black">Not a red-wine person?</strong> Ask for
                the white gløgg, or the alcohol-free version — nearly every spot has both.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* planner CTA */}
      <section className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Found your glass?<br />
            <span className="font-editorial italic font-600">Build it into your days</span>
          </h2>
          <Link
            to="/planner"
            data-testid="glogg-to-planner"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
          >
            Open the planner
          </Link>
        </div>
      </section>

      <GygTourBand tourId="621540" label="SWEET TOOTH? THERE'S A PASTRY WALK" />
      <ExploreLinks
        testid="explore-glogg"
        links={[
          { label: "Markets, lights & J-Day — Christmas in Copenhagen", to: "/christmas" },
          { label: "The Christmas walk — one route through the markets", to: "/christmas-walk" },
          { label: "Where to eat it — 16 julefrokost restaurants", to: "/christmas-food" },
          { label: "The rest of the Danish table — the food guide", to: "/danish-food" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section data-testid="glogg-faq" className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Reveal>
            <h2 className="font-display font-900 tracking-tighter text-3xl md:text-4xl mb-8">
              Gløgg questions, <span className="font-editorial italic font-600">answered</span>
            </h2>
          </Reveal>
          <div className="divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
            {FAQS.map((f, i) => (
              <details key={f.q} data-testid={`glogg-faq-${i}`} className="group py-5">
                <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                  {f.q}
                  <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar title="Copenhagen Gløgg Guide" />
    </main>
  );
}
